import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, CheckSquare, CheckCircle, XCircle, HelpCircle, RotateCcw, Award } from "lucide-react";
import { quizApi } from "../lib";
import { showSuccess, showInfo } from "../lib/toast";

const XP_PER_QUIZ = 100;
const XP_PERFECT_QUIZ_BONUS = 50;

interface QuizData {
  _id: string;
  title: string;
  questions: any[];
  timeLimit?: number;
  passingScore?: number;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export default function TakeQuizPage() {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const [quiz, setQuiz] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [resultData, setResultData] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option.trim();
    return (option.optionText || option.text || option.label || "").trim();
  };

  const getOptionId = (option: any, idx: number) => {
    // Try to get the option ID from various possible fields
    if (option?.id) return option.id;
    if (option?.optionId) return option.optionId;
    if (option?._id) return option._id;
    // If no ID, use a generated ID based on content and index
    return `${idx}_${getOptionLabel(option).substring(0, 10)}`;
  };

  const getQuestionId = (q: any) => {
    // Get question ID from various possible fields
    return q._id || q.id || `q_${currentQuestion}`;
  };

  const getCorrectAnswerId = (q: any) => {
    if (!q) return null;
    return q.correctOptionId ?? q.correctAnswer ?? q.correctId ?? null;
  };

  useEffect(() => {
    const fetchQuiz = async () => {
      if (!moduleId) return;
      try {
        const res = await quizApi.getModuleQuiz(moduleId);
        if (res.success && res.data) {
          console.log("Quiz data:", res.data);
          setQuiz(res.data);
          setTimeLeft(res.data.timeLimit ? res.data.timeLimit * 60 : null);
        } else {
          setError("Quiz not found");
        }
      } catch (err) {
        console.error("Failed to fetch quiz:", err);
        setError("Failed to load quiz");
      } finally {
        setLoading(false);
      }
    };
    fetchQuiz();
  }, [moduleId]);

  useEffect(() => {
    if (!quiz || !courseId) return;
    try {
      const saved = localStorage.getItem(`quiz_submitted_${courseId}_${quiz._id}`);
      if (saved) {
        const data = JSON.parse(saved);
        setSubmitted(true);
        setScore(data.score);
        setResultData(data.resultData);
        setAnswers(data.answers || {});
      }
    } catch {}
  }, [quiz, courseId]);

  useEffect(() => {
    if (timeLeft === null || submitted) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => setTimeLeft((prev) => (prev || 0) - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, submitted]);

  const calculateScore = useCallback(() => {
    if (!quiz) return 0;
    let totalPoints = 0;
    let earnedPoints = 0;

    quiz.questions?.forEach((q: any) => {
      totalPoints += q.points || 1;
      const correctId = getCorrectAnswerId(q);
      const questionId = getQuestionId(q);
      const userAnswer = answers[questionId];
      const isCorrect = !!(correctId && userAnswer === correctId);
      if (isCorrect) earnedPoints += q.points || 1;
    });

    return totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
  }, [quiz, answers]);

  const handleSubmit = useCallback(async () => {
    const finalScore = calculateScore();
    setScore(finalScore);
    setSubmitted(true);

    let responseData = null;
    if (courseId && quiz?._id) {
      try {
        const res = await quizApi.submitQuiz(quiz._id, answers, 0);
        if (res?.success) responseData = res.data;
      } catch {
        console.error("Failed to submit quiz");
      }
    }

    setResultData(responseData);

    try {
      localStorage.setItem(
        `quiz_submitted_${courseId}_${quiz?._id}`,
        JSON.stringify({
          score: finalScore,
          answers,
          resultData: responseData,
          submittedAt: new Date().toISOString(),
        }),
      );
    } catch {}

    const passed = finalScore >= (quiz?.passingScore || 70);
    if (passed) {
      let quizXP = XP_PER_QUIZ;
      if (finalScore === 100) quizXP += XP_PERFECT_QUIZ_BONUS;
      showSuccess(`Quiz passed! +${quizXP} XP`);
    } else {
      showInfo(`Quiz completed. Score: ${Math.round(finalScore)}%`);
    }
  }, [courseId, quiz, answers, calculateScore]);

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(0);
    setCurrentQuestion(0);
    setTimeLeft(quiz?.timeLimit ? quiz.timeLimit * 60 : null);
  };

  const handleClose = () => {
    window.close();
  };

  const handleAnswerSelect = (optionId: string) => {
    if (!currentQ) return;
    
    const questionId = getQuestionId(currentQ);
    console.log("Saving answer - Question ID:", questionId, "Option ID:", optionId);
    
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-3 border-2 border-orange-500 rounded-full border-t-transparent animate-spin" />
          <p className="text-xs text-gray-500">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="mb-2 text-sm text-gray-900">{error || "Quiz not found"}</p>
          <button onClick={handleClose} className="px-4 py-2 text-xs font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    const passed = score >= (quiz.passingScore || 70);
    return (
      <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
        <div className="max-w-3xl px-4 mx-auto md:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
                <Award className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                Quiz Results
              </h1>
            </div>
            <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
              {quiz.title || "Module Quiz"} - Review your answers
            </p>
          </div>

          {/* Score Card */}
          <div className={`p-6 mb-5 text-center rounded-xl border shadow-sm ${
            passed 
              ? "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200" 
              : "bg-gradient-to-r from-red-50 to-rose-50 border-red-200"
          }`}>
            <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${
              passed ? "bg-emerald-100" : "bg-red-100"
            }`}>
              {passed ? (
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h2 className={`mb-1 text-xl font-bold ${passed ? "text-emerald-700" : "text-red-700"}`}>
              {passed ? "Quiz Passed! 🎉" : "Quiz Failed"}
            </h2>
            <p className="mb-2 text-gray-600">
              Your Score: <span className="text-xl font-bold">{Math.round(score)}%</span>
            </p>
            <p className="text-sm text-gray-500">Passing Score: {quiz.passingScore || 70}%</p>
            
            {passed && (
              <div className="inline-block p-2 mt-3 rounded-lg bg-emerald-100/50">
                <span className="text-xs font-medium text-emerald-700">+{XP_PER_QUIZ} XP Earned!</span>
              </div>
            )}
          </div>

          {/* Review Answers */}
          <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-900">Review Your Answers</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {quiz.questions?.map((q: any, idx: number) => {
                const questionId = getQuestionId(q);
                const userAnswer = answers[questionId];
                const correctId = getCorrectAnswerId(q);
                const correctOption = q.options?.find((o: any) => {
                  const optId = getOptionId(o, 0);
                  return optId === correctId;
                });
                const correctText = getOptionLabel(correctOption || {});
                const isCorrect = !!(correctId && userAnswer === correctId);

                return (
                  <div key={idx} className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      {isCorrect ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <p className="text-xs font-medium text-gray-900">
                        {idx + 1}. {q.questionText || q.question}
                      </p>
                    </div>
                    <div className="space-y-1 text-[11px] ml-6">
                      <p className="text-gray-600">
                        Your answer:{" "}
                        <span className={isCorrect ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>
                          {userAnswer ? getOptionLabel(q.options?.find((o: any) => getOptionId(o, 0) === userAnswer) || {}) : "Not answered"}
                        </span>
                      </p>
                      {!isCorrect && correctText && (
                        <p className="text-emerald-600">
                          Correct answer: <span className="font-medium">{correctText}</span>
                        </p>
                      )}
                      {q.explanation && (
                        <div className="p-2 mt-2 border rounded-lg bg-amber-50 border-amber-100">
                          <p className="text-[10px] font-medium text-amber-700">💡 Explanation:</p>
                          <p className="text-[10px] text-gray-600">{q.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 mt-5 sm:flex-row sm:justify-center">
            <button
              onClick={handleRetake}
              className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Retake Quiz
            </button>
            <button
              onClick={handleClose}
              className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = quiz.questions?.[currentQuestion];

  if (!currentQ) return null;

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = quiz.questions?.length || 0;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const currentQuestionId = getQuestionId(currentQ);
  const currentSavedAnswer = answers[currentQuestionId];

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="max-w-3xl px-4 mx-auto md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
                  <HelpCircle className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  {quiz.title || "Module Quiz"}
                </h1>
              </div>
              <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
                Answer all questions to complete the quiz
              </p>
            </div>
            {timeLeft !== null && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${
                timeLeft < 60 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
              }`}>
                <Clock className="w-3.5 h-3.5" />
                <span className="font-mono text-xs font-bold">{formatTime(timeLeft)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-[9px] text-gray-500 mb-1">
            <span>Question {currentQuestion + 1} of {totalQuestions}</span>
            <span>{answeredCount}/{totalQuestions} Answered</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-300 rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 text-xs font-bold text-white rounded-full bg-gradient-to-r from-orange-600 to-orange-500">
                {currentQuestion + 1}
              </div>
              <h2 className="text-xs font-semibold text-gray-900">
                {currentQ.questionText || currentQ.question}
              </h2>
            </div>
          </div>

          {/* Answer Options */}
          <div className="p-4 space-y-2">
            {(currentQ.options as any[])?.map((option: any, idx: number) => {
              const optionId = getOptionId(option, idx);
              const optionText = getOptionLabel(option);
              const isSelected = currentSavedAnswer === optionId;

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswerSelect(optionId)}
                  className={`w-full p-3 text-left border rounded-lg transition-all duration-200 ${
                    isSelected
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-orange-200 hover:bg-orange-50/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? "border-orange-500 bg-orange-500" : "border-gray-300"
                      }`}
                    >
                      {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </div>
                    <span className={`text-xs ${isSelected ? "text-orange-700 font-medium" : "text-gray-700"}`}>
                      {optionText}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between gap-2 p-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => currentQuestion > 0 && setCurrentQuestion((p) => p - 1)}
              disabled={currentQuestion === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-3 h-3" />
              Previous
            </button>

            {currentQuestion < totalQuestions - 1 ? (
              <button
                onClick={() => setCurrentQuestion((p) => p + 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
              >
                Next
                <ArrowRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={answeredCount < totalQuestions}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-emerald-600 to-green-500 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckSquare className="w-3 h-3" />
                Submit Quiz
              </button>
            )}
          </div>
        </div>

        {/* Answered Indicator */}
        {answeredCount < totalQuestions && (
          <div className="mt-3 text-center">
            <p className="text-[9px] text-amber-600">
              ⚠️ You've answered {answeredCount} of {totalQuestions} questions
            </p>
          </div>
        )}
      </div>
    </div>
  );
}